/**
 * Normalizes the GET /challenges/hub payload to the MissionHubContent shape.
 * The backend sends header/streak/claims/challenges/selectedCid; the log list
 * and a few display-only fields (artBg, playerName, avatar) are derived here.
 */

import { __ } from "services/Translator";

const DEFAULT_ART_BG =
  "radial-gradient(340px 240px at 70% 50%,rgba(248,216,112,.35),transparent 60%),linear-gradient(140deg,#1d4a2e,#06231a)";

export function stripHtml(value) {
  return String(value || "").replace(/<[^>]*>/g, "");
}

const RICH_TEXT_TAGS = ["b", "strong", "em", "i", "br"];

/**
 * Backend rich-text fields are injected via dangerouslySetInnerHTML, so they
 * must be neutralized here. Everything is HTML-escaped first, then only the
 * exact bare whitelisted tags (<b>, <strong>, <em>, <i>, <br>) are restored.
 * Tags carrying attributes stay escaped, so onclick/src/etc can never survive.
 */
export function sanitizeRichText(value) {
  let out = String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  RICH_TEXT_TAGS.forEach((tag) => {
    out = out
      .replace(new RegExp(`&lt;${tag}&gt;`, "gi"), `<${tag}>`)
      .replace(new RegExp(`&lt;/${tag}&gt;`, "gi"), `</${tag}>`);
  });
  out = out.replace(/&lt;br\s*\/&gt;/gi, "<br/>");
  return out;
}

/**
 * One duration format contract for the whole hub. Strips ad-hoc emoji/symbol
 * prefixes (⚡ ⏱ ✓ 🔒) and lowercases duration units so mixed backend strings
 * ("8M", "3D 0H", "⚡ 4h left") all render as "8m", "3d 0h", "4h left".
 */
export function normalizeTimeLabel(value) {
  let s = String(value || "").trim();
  if (!s) return "";
  s = s.replace(/^[^\dA-Za-z]+/, "").trim();
  s = s.replace(/(\d)\s*([DHMWdhmw])\b/g, (_, d, u) => `${d}${u.toLowerCase()}`);
  return s;
}

/** Backend pct is 0..100; the streak ring wants a 0..1 fraction. */
export function normalizeStreakPct(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1 ? Math.min(1, n / 100) : n;
}

/** Maps one shaped challenge to the log list state buckets. */
export function challengeState(challenge) {
  if (!challenge) return "new";
  if (challenge.locked) return "locked";
  if (challenge.progDone) {
    // Prefer the semantic flags newer payloads send; fall back to the
    // cta-text sniff for old payloads that only carry display strings.
    if (typeof challenge.claimable === "boolean") {
      return challenge.claimable ? "claim" : "done";
    }
    if (challenge.claimed === true) return "done";
    return String(challenge.cta || "").toLowerCase().startsWith("claim")
      ? "claim"
      : "done";
  }
  return Number(challenge.progPct) > 0 ? "active" : "new";
}

/**
 * The CTA deeplink is admin-entered free text; only a same-site path may
 * reach history.push (no external URLs, no protocol-relative //host tricks).
 */
export function sanitizeCtaRoute(value) {
  const route = typeof value === "string" ? value.trim() : "";
  return route.startsWith("/") && !route.startsWith("//") ? route : null;
}

export function normalizeChallenge(raw) {
  const challenge = raw && typeof raw === "object" ? raw : {};
  const tier = challenge.tier || "bronze";
  return {
    ...challenge,
    tier,
    tierLabel:
      challenge.tierLabel || tier.charAt(0).toUpperCase() + tier.slice(1),
    artBg: challenge.artBg || DEFAULT_ART_BG,
    game: challenge.game || challenge.title || "",
    time: normalizeTimeLabel(challenge.time),
    timeUrgent: !!challenge.timeUrgent,
    title: challenge.title || "",
    desc: sanitizeRichText(challenge.desc),
    tip: sanitizeRichText(challenge.tip),
    progLabel: challenge.progLabel || __("Progress"),
    progValue: challenge.progValue || "",
    progPct: Number(challenge.progPct) || 0,
    progDone: !!challenge.progDone,
    rewards: (Array.isArray(challenge.rewards) ? challenge.rewards : []).map(
      (r) =>
        r && typeof r === "object"
          ? { ...r, amount: r.amount == null ? "" : String(r.amount) }
          : r
    ),
    cta: challenge.cta || __("Play now"),
    ctaRoute: sanitizeCtaRoute(challenge.ctaRoute),
    locked: !!challenge.locked,
    claimable:
      typeof challenge.claimable === "boolean" ? challenge.claimable : undefined,
    claimed: typeof challenge.claimed === "boolean" ? challenge.claimed : undefined,
  };
}

export function deriveLogItems(challenges) {
  return Object.entries(challenges || {}).map(([cid, challenge]) => {
    const state = challengeState(challenge);
    return {
      cid,
      state,
      artBg: challenge.artBg,
      artLabel: challenge.game,
      tier: challenge.tier,
      title: challenge.title,
      description: stripHtml(challenge.desc),
      progPct: challenge.progPct,
      progLabel: `${Math.round(challenge.progPct)}%`,
      progDone: challenge.progDone,
      rewards: challenge.rewards,
      rewardSmall: state === "claim" ? __("Ready") : __("Reward"),
      time: state === "claim" ? null : challenge.time,
      timeUrgent: challenge.timeUrgent,
      locked: challenge.locked,
    };
  });
}

export function normalizeMissionsHub(payload, playerName) {
  if (!payload || typeof payload !== "object") return null;

  const challenges = {};
  Object.entries(payload.challenges || {}).forEach(([cid, raw]) => {
    challenges[cid] = normalizeChallenge(raw);
  });

  const header = payload.header || {};
  const streak = payload.streak || {};
  const claims = Array.isArray(payload.claims) ? payload.claims : [];

  return {
    header: {
      playerName: header.playerName || playerName || "",
      activeCount: header.activeCount ?? 0,
      readyToClaim: header.readyToClaim ?? 0,
      fsThisWeek: header.fsThisWeek ?? 0,
      streakDays: header.streakDays ?? 0,
    },
    streak: {
      currentDays: streak.currentDays ?? 0,
      targetDays: streak.targetDays ?? 7,
      pct: normalizeStreakPct(streak.pct),
      todayReward: streak.todayReward || "",
      megaPrizeLabel: streak.megaPrizeLabel || "",
      daysToMega: streak.daysToMega ?? 0,
    },
    claims: claims.map((c) => ({
      avatar: c.avatar || String(c.name || c.who || "?").charAt(0).toUpperCase(),
      who: c.who || c.name || "",
      what: c.what || c.title || "",
      amt: c.amt || c.reward || "",
    })),
    challenges,
    log: Array.isArray(payload.log) ? payload.log : deriveLogItems(challenges),
    selectedCid: payload.selectedCid || Object.keys(challenges)[0] || null,
    isPreviewDemo: false,
  };
}

/** "100 FS + 500 CC" style summary for the claim toast. */
export function rewardSummary(rewards) {
  if (!Array.isArray(rewards) || !rewards.length) return "";
  return rewards
    .map((r) => `${r.amount} ${r.unit || ""}`.trim())
    .join(" + ");
}
